// Turnstile verification utilities
const TURNSTILE_STORAGE_KEY = 'turnstile_verification';
const TURNSTILE_EXPIRY_KEY = 'turnstile_expiry';

export interface TurnstileVerification {
  token: string;
  timestamp: number;
  expiresAt: number;
}

// Check if user is verified and token hasn't expired
export const isVerified = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    const expiry = sessionStorage.getItem(TURNSTILE_EXPIRY_KEY);
    if (!expiry) return false;
    
    const expiresAt = parseInt(expiry, 10);
    return Date.now() < expiresAt;
  } catch {
    return false;
  }
};

// Get stored verification token
export const getVerificationToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  
  try {
    if (!isVerified()) return null;
    
    const stored = sessionStorage.getItem(TURNSTILE_STORAGE_KEY);
    if (!stored) return null;
    
    const verification: TurnstileVerification = JSON.parse(stored);
    return verification.token;
  } catch {
    return null;
  }
};

// Store verification token with expiry (30 minutes)
export const storeVerification = (token: string): void => {
  if (typeof window === 'undefined') return;
  
  try {
    const now = Date.now();
    const expiresAt = now + (30 * 60 * 1000); // 30 minutes
    
    const verification: TurnstileVerification = {
      token,
      timestamp: now,
      expiresAt,
    };
    
    sessionStorage.setItem(TURNSTILE_STORAGE_KEY, JSON.stringify(verification));
    sessionStorage.setItem(TURNSTILE_EXPIRY_KEY, expiresAt.toString());
  } catch (error) {
    console.error('Failed to store Turnstile verification:', error);
  }
};

// Clear verification (for logout or manual refresh)
export const clearVerification = (): void => {
  if (typeof window === 'undefined') return;
  
  try {
    sessionStorage.removeItem(TURNSTILE_STORAGE_KEY);
    sessionStorage.removeItem(TURNSTILE_EXPIRY_KEY);
  } catch (error) {
    console.error('Failed to clear Turnstile verification:', error);
  }
};

// Check if verification is about to expire (within 5 minutes)
export const isExpiringSoon = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    const expiry = sessionStorage.getItem(TURNSTILE_EXPIRY_KEY);
    if (!expiry) return true;
    
    const expiresAt = parseInt(expiry, 10);
    const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
    return expiresAt <= fiveMinutesFromNow;
  } catch {
    return true;
  }
};
